import axios from 'axios';
import { get, omit } from 'lodash-es';
import { useEffect } from 'react';

import { ExplorerContextInterface, ExplorerDataInterface } from '@page/explorer/interface/explorer-data.interface';
import { ExplorerMenuInterface } from '@page/explorer/interface/explorer-menu.interface';
import {
  PostmanCollectionEnvsInterface,
  PostmanCollectionInterface,
  PostmanCollectionItemInterface,
} from '@page/explorer/interface/postman-collection.interface';

import { useLocalStorage } from '@hook/store/use-local-storage.hook';

export const useLoadSchema = (setValue: (data: Partial<ExplorerContextInterface>) => void) => {
  const [storageVariables] = useLocalStorage<Record<string, string>>('variables');

  useEffect(() => {
    const controller = new AbortController();
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    let timeout: NodeJS.Timeout;
    const loadSchema = async () => {
      try {
        const result = await axios<PostmanCollectionInterface>({
          url: `${import.meta.env.VITE_POSTMAN_URL}&timestamp=${new Date().getTime()}`,
          method: 'GET',
          cancelToken: source.token,
          signal: controller.signal,
        });

        const schemaVariables = get(result, ['data', 'collection', 'variable']);
        const description = get(result, ['data', 'collection', 'info', 'description']);
        const envs = JSON.parse(description.replace(/\\/g, '')) as PostmanCollectionEnvsInterface[];
        const items = get(result, ['data', 'collection', 'item']);
        const newMapData = new Map<string, ExplorerDataInterface>();

        const generateMenu = (data: PostmanCollectionItemInterface[], parentId?: string): ExplorerMenuInterface[] =>
          data.map(({ item, ...data }) => {
            newMapData.set(data.id, { ...data, parentId } as ExplorerDataInterface);

            return {
              id: data.id,
              name: data.name,
              method: get(data, ['request', 'method']),
              children: generateMenu(item ?? [], data.id),
            };
          });

        const variables = (schemaVariables as any[]).reduce(
          (acc, item) => ({
            ...acc,
            [item.key]: item.value,
          }),
          {},
        );

        setValue({
          variables: { ...variables, ...omit(storageVariables, Object.keys(variables)) },
          envs,
          menu: generateMenu(items),
          data: newMapData,
          isLoading: false,
          currentEnv: envs[0].type,
        });
      } catch (error) {
        console.error(error);
        timeout = setTimeout(loadSchema, 2000);
      }
    };

    loadSchema();

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, []);
};
